/**
 * Contact Page Scroll Animations
 * Uses GSAP with ScrollTrigger for smooth, minimal animations
 * Same pattern as homepage-animations.js
 */

(function() {
  'use strict';

  // Wait for GSAP to load
  function initContactAnimations() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.warn('GSAP or ScrollTrigger not loaded');
      return;
    }

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return;
    }

    // Skip animations in Shopify editor
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    // Default animation settings
    const defaultEase = 'power2.out';
    const defaultDuration = 0.8;

    // ========================================
    // Contact Hero Section Animation
    // ========================================
    const contactHero = document.querySelector('.contact-hero, .section-contact-hero');
    if (contactHero) {
      // Title Animation
      const heroTitle = contactHero.querySelector('.contact-hero__title');
      if (heroTitle) {
        gsap.set(heroTitle, { y: 30, opacity: 0 });
        gsap.to(heroTitle, {
          y: 0,
          opacity: 1,
          duration: defaultDuration,
          ease: defaultEase,
          scrollTrigger: {
            trigger: heroTitle,
            start: 'top 90%',
            once: true
          }
        });
      }

      // Image Animation - Scale and fade
      const heroImage = contactHero.querySelector('.contact-hero__image-wrapper');
      if (heroImage) {
        const img = heroImage.querySelector('img');
        if (img) {
          gsap.set(img, { scale: 1.1, opacity: 0 });
          gsap.to(img, {
            scale: 1,
            opacity: 1,
            duration: 1.2,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: heroImage,
              start: 'top 80%',
              once: true
            }
          });
        }
      }

      // Info/Details Section Animation
      const heroInfo = contactHero.querySelector('.contact-hero__info');
      if (heroInfo) {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: heroInfo,
            start: 'top 80%',
            once: true
          }
        });

        // Heading
        const infoHeading = heroInfo.querySelector('.contact-hero__heading');
        if (infoHeading) {
          gsap.set(infoHeading, { y: 30, opacity: 0 });
          tl.to(infoHeading, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0);
        }

        // Description
        const infoDesc = heroInfo.querySelector('.contact-hero__description');
        if (infoDesc) {
          gsap.set(infoDesc, { y: 25, opacity: 0 });
          tl.to(infoDesc, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0.15);
        }

        // Contact Items - Stagger
        const contactItems = heroInfo.querySelectorAll('.contact-hero__item');
        if (contactItems.length > 0) {
          gsap.set(contactItems, { y: 20, opacity: 0 });
          tl.to(contactItems, {
            y: 0,
            opacity: 1,
            duration: 0.6,
            ease: defaultEase,
            stagger: 0.1
          }, 0.3);
        }
      }
    }

    // ========================================
    // Contact Form Hero Section Animation
    // ========================================
    const contactForm = document.querySelector('.contact-form-hero, .section-contact-form-hero');
    if (contactForm) {
      const formTl = gsap.timeline({
        scrollTrigger: {
          trigger: contactForm,
          start: 'top 80%',
          once: true
        }
      });

      // Title
      const formTitle = contactForm.querySelector('.contact-form-hero__title');
      if (formTitle) {
        gsap.set(formTitle, { y: 30, opacity: 0 });
        formTl.to(formTitle, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0);
      }

      // Subtitle
      const formSubtitle = contactForm.querySelector('.contact-form-hero__subtitle');
      if (formSubtitle) {
        gsap.set(formSubtitle, { y: 25, opacity: 0 });
        formTl.to(formSubtitle, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0.1);
      }

      // Form Fields - Stagger by row
      const formRows = contactForm.querySelectorAll('.contact-form-hero__row');
      if (formRows.length > 0) {
        gsap.set(formRows, { y: 30, opacity: 0 });
        formTl.to(formRows, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.12
        }, 0.2);
      }

      // Submit Button
      const submitBtn = contactForm.querySelector('.contact-form-hero__submit');
      if (submitBtn) {
        gsap.set(submitBtn, { y: 20, opacity: 0 });
        formTl.to(submitBtn, { y: 0, opacity: 1, duration: defaultDuration, ease: defaultEase }, 0.5);
      }
    }

    // ========================================
    // Generic Contact Page Sections
    // ========================================
    const genericSections = document.querySelectorAll('.shopify-section:not(.section-contact-hero):not(.section-contact-form-hero):not([data-contact-anim-init])');
    genericSections.forEach(section => {
      // Mark as initialized
      section.setAttribute('data-contact-anim-init', 'true');

      const sectionContent = section.querySelector('.page-width, .container, [class*="container"]');
      if (sectionContent) {
        gsap.set(sectionContent, { y: 30, opacity: 0 });
        gsap.to(sectionContent, {
          y: 0,
          opacity: 1,
          duration: defaultDuration,
          ease: defaultEase,
          scrollTrigger: {
            trigger: section,
            start: 'top 85%',
            once: true
          }
        });
      }
    });

    // ========================================
    // Form Input Focus Animations (CSS-driven)
    // ========================================
    const formInputs = document.querySelectorAll('.contact-form-hero__input, .contact-form-hero__textarea');
    formInputs.forEach(input => {
      input.addEventListener('focus', () => {
        gsap.to(input, {
          scale: 1.01,
          duration: 0.2,
          ease: 'power2.out'
        });
      });

      input.addEventListener('blur', () => {
        gsap.to(input, {
          scale: 1,
          duration: 0.2,
          ease: 'power2.out'
        });
      });
    });

    // Refresh ScrollTrigger after images load
    window.addEventListener('load', () => {
      ScrollTrigger.refresh();
    });

    // Refresh on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 250);
    });

    console.log('Contact page animations initialized');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactAnimations);
  } else {
    initContactAnimations();
  }

  // Also try after a short delay in case GSAP loads async
  setTimeout(initContactAnimations, 100);
})();
